import { countries } from '~/globals';

import stores from '~/consts/stores';
import { UTM_MEDIUM, UTM_MEDIUM_GROUPS, UTM_MEDIUM_INTERNAL_WHOLESALE } from '~/consts/utm';

export const LE_ENTITY_AUS = 'Australia';
export const LE_ENTITY_INTL = 'International';
export const LE_ENTITIES = [LE_ENTITY_AUS, LE_ENTITY_INTL];

export const CRUISE_ITEM_TYPE = 'cruise';
export const LE_LIVE_ITEM_TYPE = 'le_live';
export const DEFAULT_ITEM_TYPE = CRUISE_ITEM_TYPE;
export const LEBT_DEFAULT_ITEM_TYPE = 'hotel';
export const FLIGHT_ITEM_TYPE = 'flight';
export const FLIGHT_PROVIDER_OPTIONS = [
  { value: 'jetstar', label: 'Jetstar' },
  { value: 'sabre', label: 'Sabre' },
  { value: 'travelfusion', label: 'Travelfusion' },
  { value: 'charter', label: 'Charter' },
] as const;

export const FLIGHT_TRAVEL_SCOPE_DOMESTIC = 'domestic';
export const FLIGHT_TRAVEL_SCOPE_INTERNATIONAL = 'international';
export const FLIGHT_TRAVEL_SCOPE_OPTIONS = [
  { value: FLIGHT_TRAVEL_SCOPE_DOMESTIC, label: 'Domestic' },
  { value: FLIGHT_TRAVEL_SCOPE_INTERNATIONAL, label: 'International' },
] as const;
export const FLIGHT_TRAVEL_SCOPE_VALUES = [FLIGHT_TRAVEL_SCOPE_DOMESTIC, FLIGHT_TRAVEL_SCOPE_INTERNATIONAL] as const;
export type FlightTravelScope = (typeof FLIGHT_TRAVEL_SCOPE_VALUES)[number];

// Provider PNR (record locator): exactly 6 alphanumeric characters.
export const FLIGHT_PNR_REGEX = /^[A-Za-z0-9]{6}$/;
export const FLIGHT_PNR_HELPER_TEXT =
  'Enter the 6-character provider PNR (letters and numbers only). Use the provider PNR, not the Airline PNR.';
export const FLIGHT_PNR_INVALID_MESSAGE =
  'PNR must be exactly 6 characters (letters and numbers only). Use the provider PNR, not the Airline PNR.';

export const OFFER_TYPES = [
  {
    value: 'flight',
    label: 'Flight',
    suppliers: [
      'Jetstar',
      'Qantas',
      'Virgin Australia',
      'Cebu Pacific',
      'Air Malaysia',
      'Cathay Pacific',
      'Singapore Airlines',
      'IndiGo',
      'British Airways',
      'Fiji Airways',
      'China Airlines',
      'Japan Airlines',
      'Other',
    ],
  },
  {
    value: 'ics_store',
    label: 'ICS Store',
    suppliers: ['ICS', 'Other'],
  },
  {
    value: LE_LIVE_ITEM_TYPE,
    label: 'LE LIVE',
    enabled: false,
    suppliers: ['Other'],
  },
  {
    value: 'cruise',
    label: 'Cruise',
    suppliers: [
      'AmaWaterways',
      'APT',
      'Azamara Cruises',
      'Carnival',
      'Celebrity',
      'Creative Cruising',
      'Crystal Cruises',
      'Cruising Power',
      'Cunard',
      'Disney',
      'Emerald Cruises',
      'Explora Journey',
      'Holland America',
      'Hurtigruten',
      'Norwegian Cruises',
      'Ponant',
      'Princess Cruises',
      'Regent Seven Seas',
      'Revelex',
      'Royal Carribean',
      'Scenic Tours',
      'Seabourn',
      'Silverseas',
      'South Sea Cruises',
      'Travelmarvel',
      'Viking',
      'Virgin Voyages',
      'Other',
    ],
  },
  {
    value: 'hotel',
    label: 'Hotel',
    suppliers: [
      'Club Med',
      'Expedia TAAP',
      'Hotelbeds',
      'Island Escapes',
      'JTB',
      'Qatar Stopover',
      'RoomRes',
      'Stuba',
      'TBO',
      'Webbeds',
      'Other',
    ],
  },
  {
    value: 'tour',
    label: 'Tour',
    suppliers: [
      'AAT KINGS',
      'APT',
      'Backroads',
      'Bench Africa',
      'Contiki',
      'Cosmos',
      'Costsaver',
      'G Adventures',
      'Globus',
      'Insight Vacations',
      'Intrepid',
      'Journey Beyond',
      'JTB',
      'Klook',
      'Luxury Gold',
      'On the go',
      'Outback Spirits',
      'Rocky Mountaineer',
      'Scenic Tours',
      'Tauck',
      'The Africa Safari Co',
      'Titan Travel',
      'Trafalgar',
      'Travelmarvel',
      'Viator',
      'Other',
    ],
  },
  {
    value: 'experience',
    label: 'Experience',
    suppliers: [
      'Bench Africa',
      'Bula Taxi / Epic Fiji',
      'Daytrip Transfers',
      'Driveaway Holidays',
      'Expedia TAAP',
      'International Rail',
      'JTB',
      'Klook',
      'Mozio Transfers',
      'Qatar Stopover',
      'Rocky Mountaineer',
      'South Sea Cruises',
      'TBO',
      'Viator',
      'Other',
    ],
  },
  {
    value: 'other',
    label: 'Other',
    suppliers: [
      'Bench Africa',
      'Driveaway Holidays',
      'GDS',
      'Jetstar Groups',
      'Journey Beyond',
      'JTB',
      'Qatar Stopover',
      'Rocky Mountaineer',
      'Singapore Airlines',
      'The Africa Safari Co',
      'Virgin Groups',
      'Other',
    ],
  },
  {
    value: 'custom-offer-package',
    label: 'Custom Offer Package',
    suppliers: ['Other'],
  },
];

export const LEBT_OFFER_TYPES = OFFER_TYPES.filter((offerType) => {
  return !['cruise', 'tour', 'ics_store'].includes(offerType.value);
});

export const REASONS = [
  { value: 'vendor_not_on_le', label: "We don't have this vendor on LE" },
  { value: 'offer_not_on_le', label: "We don't have this offer on LE" },
  { value: 'tech_issues', label: 'I had tech issues booking this offer on LE' },
  { value: 'margin_max', label: 'Margin Max' },
  { value: 'other', label: 'Other' },
];

type FlightReason = {
  value: string;
  label: string;
  /** Guidance shown under the label so agents pick the right case reason at the point of selection. */
  description?: string;
  enabled: boolean;
};

/**
 * Case reasons for flight custom offers only. The generic REASONS list above still drives the
 * hotel/cruise/tour form and the GH Store form.
 *
 * Disabled entries are retired reasons: they are never offered on a new offer, but are kept so
 * existing offers saved against them still load with their original value selected rather than
 * being coerced into "Other".
 */
const FLIGHT_REASONS: Array<FlightReason> = [
  {
    value: 'complex_multi_sector_itinerary',
    label: 'Complex / Multi-Sector Itinerary',
    description: 'Complex itinerary, 5+ sectors, RTW or itinerary requiring manual construction',
    enabled: true,
  },
  {
    value: 'margin_opportunity',
    label: 'Margin Opportunity',
    description: 'Sabre/Custom Offer used to add or improve flight margin',
    enabled: true,
  },
  {
    value: 'hold_seats_fare',
    label: 'Hold Seats / Fare',
    description: 'Seats or fare held while customer considers options, arranges funds or completes payment',
    enabled: true,
  },
  {
    value: 'online_booking_failed',
    label: 'Online Booking Failed',
    description: 'Online/Spoofing booking failed and agent moved to Sabre',
    enabled: true,
  },
  {
    value: 'flight_not_available_online',
    label: 'Flight Not Available Online',
    description: 'Required flight or itinerary is available in Sabre but not returned online',
    enabled: true,
  },
  {
    value: 'protect_accommodation_booking',
    label: 'Protect Accommodation Booking',
    description: 'Flights secured before committing to non-refundable accommodation',
    enabled: true,
  },
  {
    value: 'fare_itinerary_not_supported_online',
    label: 'Fare / Itinerary Not Supported Online',
    description: 'Required fare family, open-jaw or other routing not supported through the online flow',
    enabled: true,
  },
  {
    value: 'tours_cruise_internal_flights',
    label: 'Tours / Cruise - Internal Flights',
    description: 'Internal/domestic flights required as part of a LE Tours or Cruise bundled itinerary/package',
    enabled: true,
  },
  {
    value: 'name_change',
    label: 'Name Change',
    description: 'Customer needs to change a passenger name on an existing flight booking',
    enabled: true,
  },
  {
    value: 'date_change',
    label: 'Date Change',
    description: 'Customer needs to change travel dates on an existing flight booking',
    enabled: true,
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Genuine exception not covered above - agent must provide notes',
    enabled: true,
  },
  { value: 'vendor_not_on_le', label: "We don't have this vendor on LE", enabled: false },
  { value: 'offer_not_on_le', label: "We don't have this offer on LE", enabled: false },
  { value: 'tech_issues', label: 'I had tech issues booking this offer on LE', enabled: false },
  { value: 'margin_max', label: 'Margin Max', enabled: false },
];

export function getFlightReasonsList(selectedValue?: string): Array<FlightReason> {
  return FLIGHT_REASONS.filter((option) => option.enabled || option.value === selectedValue);
}

/** Every reason value a flight offer can legitimately hold, retired ones included. */
export const FLIGHT_REASON_VALUES = FLIGHT_REASONS.map((option) => option.value);

export const FLEXIBLE_CANCELLATION_POLICY = 'flexible';
export const NONREFUNDABLE_CANCELLATION_POLICY = 'nonrefundable';

export const CUSTOM_OFFER_CANCELLATION_POLICY_TYPES = [FLEXIBLE_CANCELLATION_POLICY, NONREFUNDABLE_CANCELLATION_POLICY];

export const PURCHASE_TYPE_SALES = 'sales';
export const PURCHASE_TYPE_POST_PURCHASE = 'post-purchase';
export type PurchaseType = typeof PURCHASE_TYPE_SALES | typeof PURCHASE_TYPE_POST_PURCHASE;

export const DEFAULT_CRUISE_POST_PURCHASE_LE_FEE = 50;
export const CRUISE_POST_PURCHASE_TYPE_REFARE = 're_fare';

export const CRUISE_POST_PURCHASE_TYPES = [
  { value: 'date_change', label: 'Date Change' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'name_correction', label: 'Name Correction' },
  { value: 'change_of_sailing', label: 'Change of Sailing' },
  { value: CRUISE_POST_PURCHASE_TYPE_REFARE, label: 'Re-fare' },
  { value: 'add_passengers', label: 'Add Passenger(s)' },
  { value: 'add_ons', label: 'Add-ons' },
] as const;

export type CruisePostPurchaseType = (typeof CRUISE_POST_PURCHASE_TYPES)[number]['value'];
export const CRUISE_POST_PURCHASE_TYPE_VALUES = CRUISE_POST_PURCHASE_TYPES.map(({ value }) => value);

// Order-relationship type carried on `linked_orders`. Must stay in lockstep with
// svc-order's OrderRelationshipType, which the published contract types as a plain
// string rather than a union, so there is nothing to import yet.
export const FLIGHT_POST_PURCHASE_FEE_RELATIONSHIP = 'flight_post_purchase_fee';

export const POST_PURCHASE_TYPES = [
  { value: 'cancel_and_refund', label: 'Cancel And Refund' },
  { value: 'date_change', label: 'Date Change' },
  { value: 'credit_ticket_rebooking', label: 'Credit Ticket Rebooking' },
  { value: 'destination_change', label: 'Destination Change' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'name_correction', label: 'Name Correction' },
  { value: 'bags', label: 'Bags' },
  { value: 'advance_seats_selection', label: 'Advance Seats Selection' },
  { value: 'meals', label: 'Meals' },
] as const;

/**
 * The values svc-offer accepts for `post_purchase_type`, derived from the list above so a
 * rename here breaks every consumer at compile time instead of silently producing a value
 * the offer form's `oneOf` rejects.
 */
export type PostPurchaseType = (typeof POST_PURCHASE_TYPES)[number]['value'];
export const POST_PURCHASE_TYPE_VALUES = POST_PURCHASE_TYPES.map(({ value }) => value);

// Post-purchase type for which the auto-calculated cost price may legitimately be $0
// (a name correction often carries no vendor fee). `satisfies PostPurchaseType` keeps this
// in sync with POST_PURCHASE_TYPES: renaming the enum value fails compilation here.
export const POST_PURCHASE_TYPE_NAME_CORRECTION = 'name_correction' satisfies PostPurchaseType;

export const COUNTRIES_LIST = ['Unspecified', ...countries];

export const SALES_ATTRIBUTION_MARGIN_MAX_VALUE = 'Margin_Max';
export const SALES_ATTRIBUTION_CS_ERROR = 'cs_error';

const SALES_ATTRIBUTION_LIST = [
  { label: 'Contact Centre', value: UTM_MEDIUM, enabled: true },
  { label: 'Chadstone Store', value: stores.chadstone_store.utmMedium, enabled: true },
  { label: 'Bondi Store', value: stores.bondi_store.utmMedium, enabled: true },
  { label: stores.le_live.displayName, value: stores.le_live.utmMedium, enabled: false },
  { label: 'Margin Max', value: SALES_ATTRIBUTION_MARGIN_MAX_VALUE, enabled: true },
  { label: 'Group Bookings', value: UTM_MEDIUM_GROUPS, enabled: true },
  { label: 'Internal Wholesale', value: UTM_MEDIUM_INTERNAL_WHOLESALE, enabled: true },
  { label: 'CS Error', value: SALES_ATTRIBUTION_CS_ERROR, enabled: true },
];

export function getSalesAttributionList(selectedValue?: string) {
  return SALES_ATTRIBUTION_LIST.filter((option) => option.enabled || option.value === selectedValue);
}

export const WHOLESALE_AGENT_LIST = [
  'Ben Admon',
  'Deborah Caravello',
  'Tammy Cipriano',
  'Eleni Iordanidis',
  'Jenney Deane',
  'Matt McNeil',
  'Meaghan Couttas',
  'Melissa Crees',
  'Noel Regan',
  'Michael Finemore',
  'Cory Clark',
  'Other',
];

export const CONCIERGE_AGENT_LIST = [
  'Vicki Walls',
  'Leah Morrison',
  'Donna Lawrence',
  'Nicole Hughes',
  'Lenore Kennedy',
  'Other',
];
